import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, ShieldCheckIcon, UsersIcon, CalendarCheckIcon, FileTextIcon, BarChart3Icon, CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, WrenchIcon, HomeIcon, HeartPulseIcon, UtensilsIcon, DumbbellIcon, StoreIcon, HammerIcon, BuildingIcon, CarIcon, ScissorsIcon, BikeIcon, BedDoubleIcon, CookingPotIcon, BriefcaseIcon, PhoneIcon, StarIcon, MapPinIcon, MessageSquareIcon, CameraIcon, LaptopIcon, ShoppingCartIcon, BookOpenIcon, MailIcon } from 'lucide-react';
import { FinalCtaSection } from './FinalCtaSection';
// Icon mapping helper
const IconMap: Record<string, React.ElementType> = {
  ShieldCheckIcon,
  SearchIcon,
  TrendingUpIcon,
  UsersIcon,
  CalendarCheckIcon,
  FileTextIcon,
  BarChart3Icon,
  WrenchIcon,
  HomeIcon,
  HeartPulseIcon,
  UtensilsIcon,
  DumbbellIcon,
  StoreIcon,
  HammerIcon,
  BuildingIcon,
  CarIcon,
  ScissorsIcon,
  BikeIcon,
  BedDoubleIcon,
  CookingPotIcon,
  BriefcaseIcon,
  PhoneIcon,
  StarIcon,
  CheckCircleIcon,
  MapPinIcon,
  MessageSquareIcon,
  CameraIcon,
  LaptopIcon,
  ShoppingCartIcon,
  BookOpenIcon,
  MailIcon
};
export interface WebsiteOnderdeel {
  icon: string;
  title: string;
  description: string;
}
export interface Functionaliteit {
  icon: string;
  title: string;
  description: string;
}
export interface SiblingCluster {
  name: string;
  slug: string;
}
export interface FaqItem {
  q: string;
  a: string;
}
export interface ClusterPageProps {
  clusterName: string;
  sectorName: string;
  sectorSlug: string;
  heroTitle: string;
  heroDescription: string;
  websiteOnderdelen: WebsiteOnderdeel[];
  functionaliteiten: Functionaliteit[];
  siblingClusters: SiblingCluster[];
  faqs: FaqItem[];
}
export function ClusterPageTemplate({
  clusterName,
  sectorName,
  sectorSlug,
  heroTitle,
  heroDescription,
  websiteOnderdelen,
  functionaliteiten,
  siblingClusters,
  faqs
}: ClusterPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Website voor ${clusterName} | ${sectorName} | Noord`;
    // Meta description
    const desc = heroDescription.length > 155 ? heroDescription.substring(0, 155) + '...' : heroDescription;
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = desc;
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': `Website voor ${clusterName} | ${sectorName} | Noord`,
      'og:description': desc,
      'og:type': 'website',
      'og:url': `https://noorddigital.nl/sector/${sectorSlug}/${clusterName.toLowerCase().replace(/\s+/g, '-')}`,
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    // JSON-LD: FAQPage schema
    if (faqs.length > 0) {
      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a
          }
        }))
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(faqSchema);
      script.id = 'ld-faq-cluster';
      document.head.appendChild(script);
      return () => {
        const el = document.getElementById('ld-faq-cluster');
        if (el) el.remove();
      };
    }
  }, [clusterName, sectorName, faqs]);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <main className="pt-16 lg:pt-[72px]" data-id="element-285">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-286">
        <div className="absolute inset-0 pointer-events-none" data-id="element-287">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-288" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-289" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-290" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-291">
          <div className="max-w-3xl" data-id="element-292">
            <div className="flex items-center gap-2 mb-4 animate-fade-in-up" data-id="element-293">
              <a href={`#/sector/${sectorSlug}`} className="text-accent-light hover:text-white transition-colors font-semibold text-sm tracking-widest uppercase" data-id="element-294">
                {sectorName}
              </a>
              <span className="text-navy-400 text-sm" data-id="element-295">/</span>
              <span className="text-navy-300 font-semibold text-sm tracking-widest uppercase" data-id="element-296">
                {clusterName}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-297">
              {heroTitle}
            </h1>
            <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mb-8 animate-fade-in-up-delay-2" data-id="element-298">
              {heroDescription}
            </p>
            <div className="animate-fade-in-up-delay-3" data-id="element-299">
              <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-300">
                Plan een gesprek
                <ArrowRightIcon className="w-5 h-5" data-id="element-301" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Wat moet op een website */}
      <section className="py-24 bg-surface-muted" data-id="element-302">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-303">
          <div className="text-center mb-16" data-id="element-304">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-305">
              Wat moet er op een website voor een {clusterName.toLowerCase()}?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-306">
              Een goede website bevat de juiste pagina's om vertrouwen op te
              bouwen en klanten te overtuigen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-307">
            {websiteOnderdelen.map((item, index) => {
            const Icon = IconMap[item.icon] || StarIcon;
            return <div key={index} className="bg-white p-8 rounded-card shadow-soft" data-id="element-308">
                  <div className="flex items-start gap-4" data-id="element-309">
                    <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-310">
                      <Icon className="w-6 h-6 text-accent" data-id="element-311" />
                    </div>
                    <div data-id="element-312">
                      <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-313">
                        {item.title}
                      </h3>
                      <p className="text-slate-600 leading-relaxed" data-id="element-314">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Functionaliteiten */}
      <section className="py-24 bg-white" data-id="element-315">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-316">
          <div className="text-center mb-16" data-id="element-317">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-318">
              Belangrijke functionaliteiten
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-319">
              Functionaliteiten die onmisbaar zijn op een website voor een{' '}
              {clusterName.toLowerCase()}.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-320">
            {functionaliteiten.map((item, index) => {
            const Icon = IconMap[item.icon] || StarIcon;
            return <div key={index} className="border border-navy-100 rounded-card p-8 text-center shadow-soft" data-id="element-321">
                  <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center mx-auto mb-6" data-id="element-322">
                    <Icon className="w-7 h-7 text-accent" data-id="element-323" />
                  </div>
                  <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-324">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-325">
                    {item.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && <section className="py-24 bg-surface-muted" data-id="element-326">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-327">
            <div className="text-center mb-16" data-id="element-328">
              <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-329">
                Veelgestelde vragen
              </h2>
            </div>

            <div className="space-y-4" data-id="element-330">
              {faqs.map((faq, index) => <div key={index} className="bg-white rounded-xl overflow-hidden shadow-soft" data-id="element-331">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-332">
                    <span className="font-bold text-navy-900 pr-4" data-id="element-333">
                      {faq.q}
                    </span>
                    {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-334" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-335" />}
                  </button>
                  {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-336">
                      {faq.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>}

      {/* Silo footer — Sibling clusters + internal links */}
      <section className="py-16 bg-white border-y border-navy-100" data-id="element-337">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-338">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-339">
            {/* Sibling clusters */}
            <div data-id="element-340">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-341">
                Andere specialisaties in {sectorName}
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-342">
                {siblingClusters.map(cluster => <a key={cluster.slug} href={`#/sector/${sectorSlug}/${cluster.slug}`} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-343">
                    Website voor {cluster.name.toLowerCase()}
                  </a>)}
                <a href={`#/sector/${sectorSlug}`} className="text-sm text-accent font-medium mt-2 hover:text-accent-hover transition-colors" data-id="element-344">
                  ← Terug naar {sectorName}
                </a>
              </nav>
            </div>

            {/* Services */}
            <div data-id="element-345">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-346">
                Onze diensten
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-347">
                <a href="#/website-laten-maken" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-348">
                  Professionele website laten maken
                </a>
                <a href="#/seo-voor-websites" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-349">
                  SEO optimalisatie
                </a>
                <a href="#/wordpress-websites" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-350">
                  WordPress website bouwen
                </a>
                <a href="#/hosting-onderhoud" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-351">
                  Hosting en technisch beheer
                </a>
              </nav>
            </div>

            {/* Kennisbank */}
            <div data-id="element-352">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-353">
                Kennisbank
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-354">
                <a href="#/kosten-website-laten-maken" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-355">
                  Wat kost een website?
                </a>
                <a href="#/website-laten-maken-checklist" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-356">
                  Website checklist
                </a>
                <a href="#/wordpress-vs-wix" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-357">
                  WordPress of Wix kiezen?
                </a>
                <a href="#/" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-358">
                  Terug naar homepage
                </a>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-359" />
    </main>;
}